import type {
    Chef,
    DietTypes,
    KnownAllergyId,
    LifestyleTags,
    MealType,
    Recipe,
    RecipeCategory,
    User,
    UserMealPreferences,
} from '../types';
import { recipeConflictsWithAllergyExclusions } from './allergies';
import { recipeMatchesChefFilter } from './chefDisplay';
import { recipeHasDietType, recipeSuitableForMealType } from './recipeFilterEstimation';
import { LIFESTYLE_TAG_KEYS, recipeHasLifestyleTag } from './lifestyleTags';
import { isFavoriteRecipe } from './userMealPreferences';

export type RecipeSortKey = 'recent' | 'rated' | 'name' | 'prepTime';
export type RecipeVisibilityChip = 'household' | 'mine' | 'shared' | 'public' | 'favorites';

export type RecipeFilterState = {
    categories: RecipeCategory[];
    dietTypes: (keyof DietTypes)[];
    lifestyleTags: (keyof LifestyleTags)[];
    mealTypes: MealType[];
    visibilitySelections: RecipeVisibilityChip[];
    sortBy: RecipeSortKey;
    sortOrder: 'asc' | 'desc';
    chefIds: string[];
    excludeAllergens: KnownAllergyId[];
    minRating: number | null;
};

export const DEFAULT_RECIPE_FILTERS: RecipeFilterState = {
    categories: [],
    dietTypes: [],
    lifestyleTags: [],
    mealTypes: [],
    visibilitySelections: [],
    sortBy: 'recent',
    sortOrder: 'desc',
    chefIds: [],
    excludeAllergens: [],
    minRating: null,
};

/** @deprecated Legacy shape — use visibilitySelections */
export type LegacyRecipeFilterState = RecipeFilterState & {
    visibility?: 'all' | 'mine' | 'shared' | 'public' | 'household';
    favoritesOnly?: boolean;
};

export function normalizeRecipeFilterState(filters: LegacyRecipeFilterState): RecipeFilterState {
    const excludeAllergens = Array.isArray(filters.excludeAllergens) ? filters.excludeAllergens : [];

    if (Array.isArray(filters.visibilitySelections)) {
        const minRating = typeof filters.minRating === 'number' && filters.minRating >= 1 && filters.minRating <= 5
            ? filters.minRating
            : null;
        return {
            ...(filters as RecipeFilterState),
            excludeAllergens,
            minRating,
        };
    }

    const visibilitySelections: RecipeVisibilityChip[] = [];
    const legacyVisibility = filters.visibility;
    if (legacyVisibility && legacyVisibility !== 'all') {
        visibilitySelections.push(legacyVisibility);
    }
    if (filters.favoritesOnly) {
        visibilitySelections.push('favorites');
    }

    const { visibility: _v, favoritesOnly: _f, ...rest } = filters;
    return {
        ...DEFAULT_RECIPE_FILTERS,
        ...rest,
        visibilitySelections,
        excludeAllergens: Array.isArray(rest.excludeAllergens) ? rest.excludeAllergens : [],
    };
}

export type RecipeFilterContext = {
    searchQuery?: string;
    currentUserId?: string;
    activeHouseholdId?: string;
    userMealPreferences?: UserMealPreferences | null;
    chefs?: Chef[];
    allUsers?: User[];
};

export function isRecipeOwnedByUser(recipe: Recipe, currentUserId: string | undefined): boolean {
    const ownerId = recipe.ownerId?.trim();
    const userId = currentUserId?.trim();
    if (!ownerId || !userId) return false;
    return ownerId === userId;
}

export function countActiveRecipeFilters(filters: RecipeFilterState): number {
    let count = 0;
    if (filters.categories.length > 0) count++;
    if (filters.dietTypes.length > 0) count++;
    if (filters.lifestyleTags.length > 0 || filters.mealTypes.length > 0) count++;
    if (filters.visibilitySelections.length > 0) count++;
    if (filters.chefIds.length > 0) count++;
    if (filters.excludeAllergens.length > 0) count++;
    if (filters.minRating != null) count++;
    if (filters.sortBy !== 'recent') count++;
    if (filters.sortOrder !== 'desc') count++;
    return count;
}

export function filterAndSortRecipes(
    recipes: Recipe[],
    filters: RecipeFilterState,
    context: RecipeFilterContext = {},
): Recipe[] {
    const {
        searchQuery = '',
        currentUserId = '',
        activeHouseholdId = '',
        userMealPreferences = null,
        chefs = [],
        allUsers = [],
    } = context;

    const query = searchQuery.toLowerCase().trim();

    const filtered = recipes.filter((r) => {
        if (r.isDeleted) return false;

        const matchesSearch =
            !query
            || r.name.toLowerCase().includes(query)
            || r.ingredients.some((i) => i.name.toLowerCase().includes(query))
            || r.englishTranslation?.name?.toLowerCase().includes(query)
            || r.englishTranslation?.ingredients?.some((i) => i.name.toLowerCase().includes(query));

        const matchesCategory =
            filters.categories.length === 0
            || filters.categories.includes(r.category as RecipeCategory);

        const matchesDiet =
            filters.dietTypes.length === 0
            || filters.dietTypes.every((dt) => recipeHasDietType(r, dt));

        const matchesLifestyle =
            filters.lifestyleTags.length === 0
            || filters.lifestyleTags.every((tag) => recipeHasLifestyleTag(r, tag));

        const matchesMealType =
            filters.mealTypes.length === 0
            || filters.mealTypes.every((mealType) => recipeSuitableForMealType(r, mealType));

        const isSharedToHousehold = r.sharedWithHouseholds?.includes(activeHouseholdId);
        const isHouseholdRecipe = r.householdId === activeHouseholdId || isSharedToHousehold;

        const matchesVisibility =
            filters.visibilitySelections.length === 0
            || filters.visibilitySelections.some((chip) => {
                switch (chip) {
                    case 'mine':
                        return isRecipeOwnedByUser(r, currentUserId);
                    case 'shared':
                        return isSharedToHousehold;
                    case 'public':
                        return r.isPublic === true && r.isTemporary !== true;
                    case 'household':
                        return isHouseholdRecipe;
                    case 'favorites':
                        return isFavoriteRecipe(userMealPreferences, r.id);
                    default:
                        return false;
                }
            });

        const matchesChef =
            filters.chefIds.length === 0
            || filters.chefIds.some((chefId) => recipeMatchesChefFilter(r, chefId, chefs, allUsers));

        const matchesAllergyExclusion = !recipeConflictsWithAllergyExclusions(r, filters.excludeAllergens);

        const matchesRating =
            filters.minRating == null
            || (r.averageRating ?? 0) >= filters.minRating;

        return matchesSearch && matchesCategory && matchesDiet && matchesLifestyle && matchesMealType && matchesVisibility && matchesChef && matchesAllergyExclusion && matchesRating;
    });

    return [...filtered].sort((a, b) => {
        let valA: string | number;
        let valB: string | number;

        switch (filters.sortBy) {
            case 'name':
                valA = a.name.toLowerCase();
                valB = b.name.toLowerCase();
                break;
            case 'rated':
                valA = a.averageRating || 0;
                valB = b.averageRating || 0;
                break;
            case 'prepTime':
                valA = a.prepTime || 0;
                valB = b.prepTime || 0;
                break;
            case 'recent':
            default:
                valA = a.createdAt || '';
                valB = b.createdAt || '';
                if (valA === valB) {
                    valA = a.name.toLowerCase();
                    valB = b.name.toLowerCase();
                }
                break;
        }

        if (valA < valB) return filters.sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return filters.sortOrder === 'asc' ? 1 : -1;
        return 0;
    });
}
